import { createSelector } from '@reduxjs/toolkit';
import { alphaSort } from 'vet-bones/utils';
import { createSelectItem } from 'vet-bones/utils/select';

import { ProjectResourceType } from 'src/constants/project';
import { getResourceKey } from 'src/containers/ProjectDiagram/utils/resource/createResource';
import { projectSelector } from 'src/selectors/project';
import { projectFileUploadSelector } from 'src/selectors/projectFileUpload';
import { projectResourceNodeDataArraySelector } from 'src/selectors/projectNodeData';
import { projectVirtualGraphSelector } from 'src/selectors/projectVirtualGraph';
import {
  ProjectResource,
  ProjectResourceField,
} from 'src/store/state/projects';

export const projectResourceSelector = (
  projectId: string,
  resourceId: string | undefined,
  resourceType: ProjectResourceType | undefined
) =>
  createSelector(
    projectFileUploadSelector(projectId, resourceId),
    projectVirtualGraphSelector(projectId, resourceId),
    (fileUpload, virtualGraph) => {
      if (!resourceId) {
        return null;
      }

      if (resourceType === ProjectResourceType.FILE_UPLOAD) {
        return fileUpload;
      }

      if (resourceType === ProjectResourceType.VIRTUAL_GRAPH) {
        return virtualGraph;
      }

      return null;
    }
  );

export const projectResourceFieldItemsSelector = (
  projectId: string,
  resourceId: string | undefined,
  resourceType: ProjectResourceType | undefined
) =>
  createSelector(
    projectResourceSelector(projectId, resourceId, resourceType),
    (resource) =>
      ((resource?.fields || []) as ProjectResourceField[]).map(
        ({ label, value }) => createSelectItem(label, value)
      )
  );

export const projectResourcesByIdSelector = (projectId: string) =>
  createSelector(
    projectSelector(projectId),
    projectResourceNodeDataArraySelector(projectId),
    (project, resourceNodeDataArray) => {
      return Object.values({
        ...project?.fileUploadsById,
        ...project?.virtualGraphsById,
      }).reduce<Record<string, ProjectResource>>((acc, resource) => {
        // filter out deleted resources
        if (
          resourceNodeDataArray.some(
            (resourceNodeData) =>
              resourceNodeData.key === getResourceKey(resource.id)
          )
        ) {
          acc[resource.id] = resource;
        }
        return acc;
      }, {});
    }
  );

export const projectResourcesByLabelSelector = (projectId: string) =>
  createSelector(projectResourcesByIdSelector(projectId), (resources) =>
    Object.entries<ProjectResource>(resources)
      .map(([id, resource]) => ({
        id,
        label: resource.name,
      }))
      .sort(alphaSort((item) => item.label))
  );

export const projectResourceSelectItems = (projectId: string) =>
  createSelector(projectResourcesByIdSelector(projectId), (resources) =>
    Object.values<ProjectResource>(resources).sort(
      alphaSort((item) => item.name)
    )
  );
